import type { ProjectSettingsFormValues } from '../Settings/forms/Project/Context';
import type { Path } from 'react-hook-form';

import React, { useEffect, useContext } from 'react';
import { useWatch } from 'react-hook-form';

import { ProjectContext } from '@core/context';
import type { ProjectContextValue } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import { useProjectStore } from '@core/store';

import Icon from '@ui/Icon';
import Toggle from '@ui/Toggle';
import Tooltip from '@ui/Tooltip';

import BasePanel from '../components/BasePanel';
import { Page, PageContent } from '../Settings/components';
import ProjectSettingsForm from '../Settings/forms/Project';
import { useProjectSettingsFormContext } from '../Settings/forms/Project/Context';
import useLiveUpdateProjectStore from '../Settings/forms/Project/useLiveUpdateProjectStore';

import AIDropdownPreview from './AIDropdownPreview';
import classes from './index.module.scss';
import LLMTextPreview from './LLMTextPreview';

// Define the form fields that should be stored
const fields = [
  'appearance.ai.dropdown',
  'llms_txt',
  'appearance.ai.options.chatgpt',
  'appearance.ai.options.claude',
  'appearance.ai.options.clipboard',
  'appearance.ai.options.view_as_markdown',
] as const;

/**
 * The content of the AI Panel with access to the form context
 */
function AIPanelContent() {
  const bem = useClassy(classes, 'AIPanel');
  const { control, watch, setValue } = useProjectSettingsFormContext();

  // Get project context to access parent/child relationships
  const { project } = useContext(ProjectContext) as ProjectContextValue;
  const isChild = !!project.parent || (project.siblings && project.siblings.length > 0);

  // If single project enterprise, we don't want to include the subdomain in the path of the URL
  const isSingleProjectEnterprise = project.parent && project.parent.flags.singleProjectEnterprise;

  // Get the project URL for llms.txt
  const projectDomain = window.location.origin;

  // For enterprise projects, construct the URL with parent/child structure
  let llmsUrl = `${projectDomain}/llms.txt`;
  if (isChild && !isSingleProjectEnterprise) {
    // If this is a child project, URL should be parent-domain/child-subdomain/llms.txt
    llmsUrl = `${projectDomain}/${project.subdomain}/llms.txt`;
  }

  // Watch main dropdown and specific option values
  const aiDropdownEnabled = watch('appearance.ai.dropdown') === 'enabled';
  const enableChatGPT = watch('appearance.ai.options.chatgpt') === 'enabled';
  const enableClaude = watch('appearance.ai.options.claude') === 'enabled';
  const enableClipboard = watch('appearance.ai.options.clipboard') === 'enabled';
  const enableViewAsMarkdown = watch('appearance.ai.options.view_as_markdown') === 'enabled';
  const llmEnabled = watch('llms_txt') === 'enabled';

  // Get the saved state from the store to determine if we should show URL preview
  const savedLlmValue = useProjectStore(s => s.initialData.llms_txt);
  const savedLlmEnabled = savedLlmValue === 'enabled';

  // Check if docs are internal by watching privacy.view
  const privacyView = useWatch({ control, name: 'privacy.view' });
  const isInternal = privacyView && privacyView !== 'public';

  // Connect form to store for live updates
  useLiveUpdateProjectStore({
    fields: fields as unknown as Path<ProjectSettingsFormValues>[],
    control,
  });

  // Disable AI features when docs are set to internal
  useEffect(() => {
    if (isInternal) {
      setValue('appearance.ai.dropdown', 'disabled', { shouldDirty: true });
      setValue('llms_txt', 'disabled', { shouldDirty: true });
    }
  }, [isInternal, setValue]);

  // Enable all options by default when AI dropdown is enabled
  useEffect(() => {
    if (aiDropdownEnabled) {
      // Only set options to enabled if they haven't been explicitly set yet
      if (watch('appearance.ai.options.chatgpt') === undefined) {
        setValue('appearance.ai.options.chatgpt', 'enabled');
      }
      if (watch('appearance.ai.options.claude') === undefined) {
        setValue('appearance.ai.options.claude', 'enabled');
      }
      if (watch('appearance.ai.options.clipboard') === undefined) {
        setValue('appearance.ai.options.clipboard', 'enabled');
      }
      if (watch('appearance.ai.options.view_as_markdown') === undefined) {
        setValue('appearance.ai.options.view_as_markdown', 'enabled');
      }
    }
  }, [aiDropdownEnabled, setValue, watch]);

  // Toggle the main AI dropdown
  const handleToggleAIDropdown = (checked: boolean) => {
    setValue('appearance.ai.dropdown', checked ? 'enabled' : 'disabled', {
      shouldDirty: true,
    });
  };

  // Handle AI option changes
  const handleOptionChange = (option: string, checked: boolean) => {
    setValue(`appearance.ai.options.${option}` as Path<ProjectSettingsFormValues>, checked ? 'enabled' : 'disabled', {
      shouldDirty: true,
    });
  };

  // Toggle LLM integration
  const handleToggleLLM = (checked: boolean) => {
    setValue('llms_txt', checked ? 'enabled' : 'disabled', {
      shouldDirty: true,
    });
  };

  // Content for tooltip when disabled
  const disabledTooltipContent = 'Disabled for internal docs - set to public to enable';

  // Render LLM card
  const renderLLMCard = () => {
    const card = (
      <div className={llmEnabled ? bem('-card', 'active') : bem('-card', isInternal ? 'disabled' : undefined)}>
        <div className={bem('-card-toggle')}>
          <Toggle
            checked={llmEnabled}
            disabled={isInternal}
            onChange={event => !isInternal && handleToggleLLM(event.target.checked)}
            size="sm"
            type="toggle"
          />
        </div>
        <div className={bem('-card-header')}>
          <div className={bem('-card-status')}>
            <h3 className={bem('-card-title')}>LLMs.txt</h3>
          </div>
          <p className={bem('-card-description')}>
            Publish an LLMs.txt file to help AI assistants understand your docs.
          </p>
          {!!savedLlmEnabled && (
            <div className={bem('-url-preview')}>
              <a className={bem('-url-link')} href={llmsUrl} rel="noopener noreferrer" target="_blank">
                {llmsUrl}
              </a>
            </div>
          )}
        </div>
        <div className={bem('-card-content')}>
          <span className={bem('-options-label')}>Example</span>
          <div className={bem('-preview-container')}>
            <LLMTextPreview />
          </div>
        </div>
      </div>
    );

    return isInternal ? (
      <Tooltip content={disabledTooltipContent} maxWidth={180} placement="top">
        {card}
      </Tooltip>
    ) : (
      card
    );
  };

  // Render AI Dropdown card
  const renderAIDropdownCard = () => {
    const card = (
      <div className={aiDropdownEnabled ? bem('-card', 'active') : bem('-card', isInternal ? 'disabled' : undefined)}>
        <div className={bem('-card-toggle')}>
          <Toggle
            checked={aiDropdownEnabled}
            disabled={isInternal}
            onChange={event => !isInternal && handleToggleAIDropdown(event.target.checked)}
            size="sm"
            type="toggle"
          />
        </div>
        <div className={bem('-card-header')}>
          <div className={bem('-card-status')}>
            <h3 className={bem('-card-title')}>Ask AI Dropdown</h3>
          </div>
          <p className={bem('-card-description')}>Adds a dropdown for sharing docs with AI assistants.</p>
        </div>

        <div className={bem('-card-content')}>
          <span className={bem('-options-label')}>Preview</span>
          <div className={bem('-preview-container')}>
            <AIDropdownPreview
              enableChatGPT={enableChatGPT}
              enableClaude={enableClaude}
              enableClipboard={enableClipboard}
              enableViewAsMarkdown={enableViewAsMarkdown}
            />
          </div>
          {!!aiDropdownEnabled && !isInternal && (
            <div className={bem('-options-container')}>
              <span className={bem('-options-label')}>Dropdown Options</span>
              <ul className={bem('-options-list')}>
                {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                <li
                  className={enableChatGPT ? bem('-option', 'active') : bem('-option')}
                  onClick={() => handleOptionChange('chatgpt', !enableChatGPT)}
                >
                  <span className={bem('-option-name')}>ChatGPT</span>
                  <div className={bem('-option-toggle')}>
                    <Icon
                      className={enableChatGPT ? bem('-option-toggle-visible') : bem('-option-toggle-hidden')}
                      name="check"
                    />
                  </div>
                </li>

                <li
                  className={enableClaude ? bem('-option', 'active') : bem('-option')}
                  onClick={() => handleOptionChange('claude', !enableClaude)}
                >
                  <span className={bem('-option-name')}>Claude</span>
                  <div className={bem('-option-toggle')}>
                    <Icon
                      className={enableClaude ? bem('-option-toggle-visible') : bem('-option-toggle-hidden')}
                      name="check"
                    />
                  </div>
                </li>

                <li
                  className={enableClipboard ? bem('-option', 'active') : bem('-option')}
                  onClick={() => handleOptionChange('clipboard', !enableClipboard)}
                >
                  <span className={bem('-option-name')}>Copy to Clipboard</span>
                  <div className={bem('-option-toggle')}>
                    <Icon
                      className={enableClipboard ? bem('-option-toggle-visible') : bem('-option-toggle-hidden')}
                      name="check"
                    />
                  </div>
                </li>

                <li
                  className={enableViewAsMarkdown ? bem('-option', 'active') : bem('-option')}
                  onClick={() => handleOptionChange('view_as_markdown', !enableViewAsMarkdown)}
                >
                  <span className={bem('-option-name')}>View as Markdown</span>
                  <div className={bem('-option-toggle')}>
                    <Icon
                      className={enableViewAsMarkdown ? bem('-option-toggle-visible') : bem('-option-toggle-hidden')}
                      name="check"
                    />
                  </div>
                </li>
                {/* eslint-enable jsx-a11y/click-events-have-key-events */}
              </ul>
            </div>
          )}
        </div>
      </div>
    );

    return isInternal ? (
      <Tooltip content={disabledTooltipContent} maxWidth={180} placement="bottom">
        {card}
      </Tooltip>
    ) : (
      card
    );
  };

  return (
    <Page>
      <PageContent className={bem('-content-no-overflow')} isCondensed>
        <div className={bem('-panel-title')}>
          <h2>AI Features</h2>
        </div>
        <div className={bem('-cards-container')}>
          {renderLLMCard()}
          {renderAIDropdownCard()}
        </div>
      </PageContent>
    </Page>
  );
}

/**
 * AI panel component for the SuperHub layout.
 * Provides navigation for AI features such as AI Assistant, Content Generator, etc.
 */
export default function AIPanel() {
  const bem = useClassy(classes, 'AIPanel');

  return (
    <BasePanel label="AI features">
      <div className={bem('&')}>
        <ProjectSettingsForm>
          <AIPanelContent />
        </ProjectSettingsForm>
      </div>
    </BasePanel>
  );
}
